import { ReactNode } from 'react'

import { clsx } from 'clsx'

import { Event, Image } from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { EventStaticFieldIds, VisibleFieldIds } from '../../types.js'
import { EventAttendees } from '../Field/attendees.js'
import { EventDate } from '../Field/Date.js'
import { EventStatusBadges } from '../Field/EventStatusBadges.js'
import { EventHostInfo } from '../Field/HostInfo.js'
import { EventImage } from '../Field/Image.js'
import { EventLocation } from '../Field/Location.js'
import { EventFieldRsvp } from '../Field/Rsvp.js'
import { EventFieldTitle } from '../Field/Title.js'

interface Props {
  event: Event
  visibleFieldIds?: VisibleFieldIds
  activeFieldId?: EventStaticFieldIds
  className?: string
  footer?: ReactNode
  hideHeader?: boolean
  preview?: boolean
}

export const EventsListFeedViewItemBox = ({
  event,
  visibleFieldIds,
  activeFieldId,
  className,
  footer,
  hideHeader,
  preview,
}: Props) => {
  const {
    endTime,
    startTime,
    timeZone,
    title,
    physicalLocations,
    thumbnail,
    hosts,
    virtualLocations,
    recentRegistrations,
  } = event

  const isEventDetailsEnabled =
    visibleFieldIds?.[EventStaticFieldIds.EventDetails]
  const isCoverImageEnabled = visibleFieldIds?.[EventStaticFieldIds.CoverImage]
  const isHostInfoEnabled = visibleFieldIds?.[EventStaticFieldIds.HostInfo]
  const isAttendeesEnabled = visibleFieldIds[EventStaticFieldIds.Attendees]

  return (
    <>
      <div className={clsx('@container p-3', className)}>
        {!hideHeader && (
          <div className="flex items-center flex-wrap justify-between gap-1 mb-4">
            <div className="flex items-center">
              <div className="rounded-sm backdrop-brightness-125 mr-2 p-1">
                <SvgIcon name="calendar" className="text-action-primary" />
              </div>
              <T defaultMessage="Event" id="Generic.Event" />
            </div>
            <EventStatusBadges event={event} />
          </div>
        )}

        <div className="flex flex-col @sm:flex-row gap-2">
          {isCoverImageEnabled && (
            <div
              className={clsx(
                'overflow-hidden grow aspect-video w-full @sm:aspect-square @sm:grow-0 @sm:w-24 @sm:h-24 shrink-0',
                activeFieldId === EventStaticFieldIds.CoverImage &&
                  'ring-2 ring-offset-0 ring-focused rounded-base',
              )}
            >
              <EventImage
                image={thumbnail as Image}
                aspectRatio="aspect-auto"
                className="h-full w-full"
                label={title}
              />
            </div>
          )}
          <div className="border-l pl-3 grow min-w-0 flex flex-col gap-y-1">
            {isEventDetailsEnabled && (
              <div className="flex flex-col gap-1 justify-between max-w-full">
                <div className="flex items-center gap-1 flex-wrap">
                  <EventDate
                    className="flex-wrap"
                    timeZone={timeZone}
                    startTime={startTime}
                    endTime={endTime}
                    type="inline"
                    highlighted={
                      activeFieldId === EventStaticFieldIds.EventDetails
                    }
                  />
                  {isHostInfoEnabled && (
                    <EventHostInfo
                      avatarSize="1.5x"
                      className="min-w-0 flex items-center"
                      hosts={hosts}
                      highlighted={
                        activeFieldId === EventStaticFieldIds.HostInfo
                      }
                    />
                  )}
                </div>
                {hideHeader && (
                  <div className="shrink-0">
                    <EventStatusBadges event={event} />
                  </div>
                )}
              </div>
            )}
            <EventFieldTitle title={title} titleVariant="headingXs" />
            <EventLocation
              physicalLocations={physicalLocations}
              virtualLocations={virtualLocations}
            />

            <div className="flex items-center flex-wrap min-w-0 gap-2">
              {isAttendeesEnabled && (
                <EventAttendees
                  preview={preview}
                  visibleCount={3}
                  recentRegistrations={recentRegistrations}
                  totalRegistrationsCount={event.registrationsCount}
                  highlighted={activeFieldId === EventStaticFieldIds.Attendees}
                  endTime={endTime}
                />
              )}
              <div className="ms-auto min-w-0">
                <EventFieldRsvp event={event} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {footer}
    </>
  )
}
